import { makeAutoObservable } from "mobx";
import { LocalStorageConstants } from "../../../constants/LocalStorageConstants";
import { IpProject } from "../../../models/ipProject";
import QueryService from "../../../utils/QueryService";

export interface IIpProjectsStore {
	search: string;
	setSearch: (s: string) => void;

	get loading(): boolean;

	get ipProjects(): IpProject[];

	loadIpProjects: () => Promise<void>;

	selectedIpProjectId: string | null;
	setSelectedIpProjectId: (id: string | null) => void;

	updateIpProject: (id: string, patch: { name?: string; description?: string; author?: string }) => Promise<void>;
	deleteIpProject: (id: string) => Promise<void>;

	createIpProject: (payload: { name: string; description?: string; authorName?: string }) => Promise<void>;

	downloadAuditLog: (id: string) => Promise<void>;
}

export default class IpProjectsStore implements IIpProjectsStore {
	private queryService: QueryService = new QueryService("/ip_projects");
	search: string = "";
	private _ipProjects: IpProject[] = [];
	private _loading: boolean = false;
	selectedIpProjectId: string | null = null;

	constructor() {
		makeAutoObservable(this);

		// Reset selected Ip Project Id on store init
		localStorage.removeItem(LocalStorageConstants.IP_PROJECT_ID);
	}

	setSearch = (s: string) => {
		this.search = s;
	};

	setSelectedIpProjectId = (id: string | null) => {
		this.selectedIpProjectId = id;
		if (id) {
			localStorage.setItem(LocalStorageConstants.IP_PROJECT_ID, id);
		} else {
			localStorage.removeItem(LocalStorageConstants.IP_PROJECT_ID);
		}
	};

	get loading(): boolean {
		return this._loading;
	}

	get ipProjects(): IpProject[] {
		const all = this._ipProjects;
		if (!this.search) return all;
		const v = this.search.toLowerCase();
		return all.filter((p) => {
			const name = p.name ?? "";
			const authorName = p.author ?? "";
			return name.toLowerCase().includes(v) || authorName.toLowerCase().includes(v);
		});
	}

	loadIpProjects = async () => {
		this._loading = true;
		try {
			const res = await this.queryService.get(`/`);
			this._ipProjects = res || [];
		} finally {
			this._loading = false;
		}
	};

	updateIpProject = async (id: string, patch: { name?: string; description?: string; author?: string }) => {
		const updated: IpProject = await this.queryService.patch(`/${id}`, patch);
		this._ipProjects = this._ipProjects.map((p) => (p.id === id ? { ...p, ...updated } : p));
	};

	deleteIpProject = async (id: string) => {
		await this.queryService.delete(`/${id}`);
		this._ipProjects = this._ipProjects.filter((p) => p.id !== id);
	};

	createIpProject = async (payload: { name: string; description?: string; authorName?: string }) => {
		await this.queryService.post(`/`, {
			name: payload.name,
			description: payload.description,
			author: payload.authorName,
		});
		await this.loadIpProjects();
	};

	downloadAuditLog = async (id: string) => {
		const res = await this.queryService.get(`/${id}/export_audit_log`);
		const zipUrl: string = res?.zip_url;
		const filename: string = res?.filename || "ip_project_audit.zip";
		if (!zipUrl) return;
		const a = document.createElement("a");
		a.href = zipUrl;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};
}
